<template>
  <div class="vehicle-result-container column" ref="resultContainer">
    <VehicleResultFilterDrawer
      v-model="showRefineSearchModal"
      :searchOptions="searchOptions"
      :make-facet-option-count="vehicleResultMakeFacetOptionCount"
      @update:searchOptions="applySearchOptions"
      :sort-by.sync="sortBy"
      :sort-order.sync="sortOrder"
      :sort-options="sortOptions"
      :repayment.sync="repayment"
      :formatted-vehicles="formattedVehicles"
      :has-items-in-short-list="hasItemsInShortList"
      :submit-loading="ratebookLoading"
      :disabled="isFetchRatebookLoading"
      :pageSize="pageSize"
      :leaseTerm="leaseTerm"
      @submit="handleSearch"
      @leaseInclusion="handleLeaseInclusionDialog"
      @reset="onResetFilters"
      ref="vehicleResultFilterDrawer"
    />
    <LeaseInclusionModal v-model="showLeaseInclusion" />
    <InformationModal v-model="showInformationModal" @onClose="showInfo(false)" />
    <EnquireNowModal v-model="showEnquireNowModal" @onClose="showEnquireNow(false)" :vehicle="enquireNowVehicle" />
    <div class="vehicle-result-header">
      <div class="lease-inclusion-container col-12 q-mb-md">
        <p class="lease-inclusion-intro text-center text-weight-bold">
          Your lease includes exclusive use of the vehicle and most running costs bundled into one fixed monthly payment.
        </p>
        <LeaseInclusionVehicleResult class="lease-inclusion-desktop" />
      </div>
    </div>
    <MobileNavigation
      :showRefineSearchModal.sync="showRefineSearchModal"
      :search-options="searchOptions"
      :hasItemsInShortList="hasItemsInShortList"
      :isCalculatorButtonRounded="getIsCalculatorButtonRounded"
      :make-facet-option-count="vehicleResultMakeFacetOptionCount"
      @viewShortlist="handleViewShortlist"
    />
    <!-- SEPERATE THIS AS OWN COMPONTENT-->
    <div class="vehicle-result-body row items-start q-col-gutter-sm" v-if="!isFetchRatebookLoading">
      <div class="action-group-refine-search-desktop col-12 col-md-3 col-lg-4 col-xl-4">
        <CustomExpansionItem
          class="col-12"
          :icon="sliderIcon"
          :headerStyle="{ color: 'var(--q-color-primary)' }"
          label="Refine search"
          headerClass="search-filter"
          v-model="showRefineSearch"
        >
          <RefineSearchContainer
            ref="refine"
            class="refine-search"
            :value="searchOptions"
            @input="applySearchOptions"
            :sortedBy="sortBy"
            :sortedOrder="sortOrder"
            :disabled="isFetchRatebookLoading"
            :pageSize="pageSize"
            :leaseTerm="leaseTerm"
            @submit="handleSearch"
          />
        </CustomExpansionItem>
      </div>
      <div class="col-12 col-md-9 col-lg-8 col-xl-8">
        <VehicleResultActions
          :sortBy.sync="sortBy"
          :sortOrder="sortOrder"
          :sortOptions="sortOptions"
          :ratebookLoading="ratebookLoading"
          :formattedVehicles="formattedVehicles"
          :repayment.sync="repayment"
          :leaseTerm.sync="leaseTerm"
          :leaseTermOptions="leaseTermOptions"
          :hasItemsInShortList="hasItemsInShortList"
          :isCalculatorButtonRounded="getIsCalculatorButtonRounded"
          :showRefineSearch="showRefineSearch"
          :showRefineSearchContainer.sync="showRefineSearchContainer"
          :searchOptions="searchOptions"
          @update:searchOptions="applySearchOptions"
          :disabled="isFetchRatebookLoading"
          @sort="handleSort"
          @sortOrder="handleSortOrder"
          @viewShortlist="handleViewShortlist"
          @leaseInclusion="handleLeaseInclusionDialog"
          @search="handleSearch"
          ref="vehicleResultActions"
        />
        <ShortlistSection
          :shortlist="shortlist"
          :user="user"
          :repayment="repayment"
          :showShortlistedVehicles.sync="showShortlistedVehicles"
          :disableShortlist="disableShortlist"
          @removeShortlist="handleRemoveShortlist"
          @input="handleInput"
          @infoIconClick="showInfo"
          @enquireNow="showEnquireNow"
          @pricingInfoIconClick="showDisclaimerDialog"
        />

        <div class="vehicle-result-list col-12">
          <div v-for="vehicle in formattedVehicles" :key="vehicle.catalogVehicleId">
            <VehicleResultCard
              v-if="!isVehicleShortlisted(vehicle)"
              class="q-mb-md"
              v-model="vehicle.isShortlisted"
              :imagePath="vehicle.jatoImagepath"
              :imagePathBase="vehicle.jatoImagepathBase"
              :imagePathTrim="vehicle.jatoImagepathTrim"
              :fuelType="`${vehicle.catalogYear} | ${vehicle.vehicleFueltype} | ${vehicle.vehicleTransmissiongroup}`"
              :vehicleDescription="formatVehicleDescription(vehicle)"
              :vehicleName="`${formatWordsAsSentences(vehicle.vehicleMake)} ${formatWordsAsSentences(vehicle.vehicleModel)} ${
                vehicle.vehicleTrim || ''
              } |  ${vehicle.vehicleDrivetrain}`"
              :weeklyPrice="vehicle.weekLeaseprice"
              :monthlyPrice="vehicle.monthLeaseprice"
              :rrp="vehicle.consumerPrice"
              :repaymentOption="repayment"
              :catalogVehicleId="vehicle.catalogVehicleId"
              :vehicleMake="vehicle.vehicleMake"
              :vehicleModel="vehicle.vehicleModel"
              :vehicleTrim="vehicle.vehicleTrim || ''"
              :vehicleDrivetrain="vehicle.vehicleDrivetrain || ''"
              :vehicleFinance="vehicle.vehicleFinance"
              :vehicleFinanceWeekly="vehicle.vehicleFinanceWeekly"
              :vehicleRunningCost="vehicle.vehicleRunningCost"
              :vehicleRunningCostWeekly="vehicle.vehicleRunningCostWeekly"
              :vehicle="vehicle"
              @input="handleInput(vehicle)"
              @onInfoIconClick="showInfo(true)"
              @onHandleEnquireNow="showEnquireNow(true, vehicle)"
              @onPricingInfoIconClick="showDisclaimerDialog"
              :disabled="disableShortlist"
            />
          </div>
          <NoResults v-if="!ratebookLoading && formattedVehicles.length === 0" @reset="onResetFilters" />
          <LoadingSpinner v-if="ratebookLoading && formattedVehicles.length === 0" />

          <div
            v-if="!isFetchRatebookLoading && formattedVehicles.length > 0"
            class="full-width row justify-center items-center q-col-gutter-sm"
          >
            <QSelect outlined v-model="pageSize" :options="pageSizeOptions" @input="handlePageSize">
              <template v-slot:prepend> <span class="text-grey-6" style="font-size: 14px"> Records per page: </span> </template>
            </QSelect>
            <div class="text-grey-6">{{ currentPageRange }}</div>
            <QPagination color="primary" v-model="currentPage" :max-pages="6" @input="fetchVehicles" :max="totalPages" direction-links />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QSelect, QPagination } from 'quasar';
import { evaOptions2, evaChevronRight } from '@quasar/extras/eva-icons';
import { mapActions, mapGetters } from 'vuex';
import CustomExpansionItem from '../../components/Calculator/CustomExpansionItem';
import VehicleResultCard from '../../components/Calculator/VehicleResult/VehicleResultCard';
import RefineSearchContainer from '../../components/Calculator/RefineSearch/RefineSearchContainer';
import LeaseInclusionVehicleResult from '../../components/Calculator/LeaseInclusion/LeaseInclusionVehicleResult';
import LeaseInclusionModal from '../../components/Calculator/LeaseInclusion/LeaseInclusionModal';
import NoResults from '../../components/Calculator/VehicleResult/NoResults.vue';
import VehicleResultFilterDrawer from '../../components/Calculator/VehicleResult/VehicleResultFilterDrawer.vue';
import MobileNavigation from '../../components/Calculator/VehicleResult/MobileNavigation.vue';
import VehicleResultActions from '../../components/Calculator/VehicleResult/VehicleResultActions.vue';
import ShortlistSection from '../../components/Calculator/VehicleResult/ShortlistSection.vue';
import LoadingSpinner from '../../components/Calculator/LoadingSpinner.vue';
import {
  fetchRatebookMixin,
  shortListOperationsMixin,
  disclaimerDialogMixin,
  vehicleResultPageShortlistMixin,
  vehicleResultPageSearchSortMixin,
  vehicleResultPageModalMixin
} from '../../mixins/calculator';
import { formatWordsAsSentences } from '../../utils/calculator/wordFormatter';
import { formatVehicleDescription } from '../../utils/calculator/vehicleFormatter';
import { DEFAULT_LEASE_TERM, LEASE_TERM_OPTIONS, RATEBOOK_SORT_WEEK_LEASE_PRICE } from '../../utils/constants';
import { getDefaultLeaseTermOption } from '../../utils/calculator/leaseTermUtils';
import InformationModal from '../../components/Calculator/Modal/InformationModal.vue';
import EnquireNowModal from '../../components/Calculator/Modal/EnquireNowModal.vue';

export default {
  name: 'VehicleResultPage',
  data() {
    return {
      searchOptions: {},
      isMakeOpen: false,
      showRefineSearch: true,
      showRefineSearchContainer: true,
      showRefineSearchModal: false,
      showShortlistedVehicles: false,
      isModelOpen: false,
      initialLoading: true,
      totalPages: 0,
      pageSizeOptions: [10, 25, 50, 100],
      selectedMakes: [],
      enquireNowVehicle: null,
      sortBy: {
        label: 'Make',
        value: 'vehicle_description'
      },
      sortOrder: 'ASC',
      currentPage: 1,
      pageSize: 25,
      repayment: 'weekly',
      leaseTerm: {},
      repaymentSelect: { value: 'weekly', label: 'Weekly' },
      disableShortlist: false,
      // Flag to prevent leaseTerm watcher from firing during initial load
      isInitializingLeaseTerm: true,
      sortOptions: [
        {
          label: 'RRP',
          value: 'consumer_price'
        },
        {
          label: 'Rental price',
          value: RATEBOOK_SORT_WEEK_LEASE_PRICE
        },
        {
          label: 'Make',
          value: 'vehicle_description'
        }
      ],
      repaymentOptions: [
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' }
      ],
      showLeaseInclusion: false,
      showInformationModal: false,
      showEnquireNowModal: false,
      defaultLeaseTerm: DEFAULT_LEASE_TERM,
      leaseTermOptions: LEASE_TERM_OPTIONS
    };
  },
  mixins: [
    fetchRatebookMixin,
    shortListOperationsMixin,
    disclaimerDialogMixin,
    vehicleResultPageShortlistMixin,
    vehicleResultPageSearchSortMixin,
    vehicleResultPageModalMixin
  ],
  watch: {
    showRefineSearch(value) {
      // Sync showRefineSearchContainer with showRefineSearch
      // QSlideTransition handles the animation smoothly, no debounce needed
      this.showRefineSearchContainer = value;
    },
    queryParams(value) {
      this.applySearchOptions(value);
    },
    async repayment(value, oldValue) {
      this.repaymentSelect = this.repaymentOptions.find((repayment) => repayment.value == value);
      await this.vrpSearch_onRepaymentChange(value, oldValue);
    },
    repaymentSelect(newValue) {
      this.repayment = newValue.value;
    },
    leaseTerm(newValue, oldValue) {
      // Prevent watcher from firing during initial load to avoid duplicate fetchRatebook calls
      if (this.isInitializingLeaseTerm) {
        return;
      }

      // Skip if this is the initial set (oldValue is undefined) or value hasn't actually changed
      if (!oldValue || newValue?.value === oldValue?.value) {
        return;
      }

      // When lease term changes, refetch vehicles and shortlist with new lease term
      if (newValue && newValue.value) {
        this.vrpSearch_fetchUserShortlist();
        this.vrpSearch_fetchVehicles();
      }
    },
    isFetchRatebookLoading(value) {
      this.setAppLoading(value);
    }
  },
  computed: {
    ...mapGetters({
      orgProfile: 'theme/getOrgProfile',
      user: 'auth/user',
      vehicleApplication: 'vehicleApplication/vehicleApplication',
      getIsCalculatorButtonRounded: 'theme/getIsCalculatorButtonRounded',
      vehicleSearchConfig: 'vehicleSearchConfig/getVehicleSearchConfig'
    }),
    getDefaultLeaseTermOption() {
      return getDefaultLeaseTermOption(this.leaseTermOptions, this.defaultLeaseTerm);
    },
    formattedVehicles() {
      const results = this.ratebook?.results || [];

      return results.map((vehicle) => ({
        isShortlisted: this.vrpShortlist_isVehicleShortlisted(vehicle),
        ...vehicle,
        vehicleTrim: vehicle?.vehicleTrim || ''
      }));
    },
    hasItemsInShortList() {
      // Returns true when shortlist HAS items, false when empty
      // This matches the semantic meaning of the prop name used in child components
      return (this.shortlist || []).length > 0;
    },
    /** Aligns with RefineSearchContainer `makes.length` / mobile hub Make visibility. */
    vehicleResultMakeFacetOptionCount() {
      const list = this.vehicleSearchConfig?.make;
      return Array.isArray(list) ? list.length : 0;
    },
    currentPageRange() {
      const total = this.ratebook?.resultCount || 0;

      if (!total) return '';
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(start + this.pageSize - 1, total);
      return `${start}-${end} of ${total}`;
    }
  },
  methods: {
    ...mapActions({
      setAppLoading: 'app/setAppLoading'
    }),
    // Wrapper methods for backward compatibility - these call the prefixed mixin methods
    // This allows templates and child components to use the original method names
    // while making it clear in the code which mixin provides each method

    // Shortlist mixin wrappers
    handleRemoveShortlist(value) {
      return this.vrpShortlist_handleRemoveShortlist(value);
    },
    handleInput(value) {
      return this.vrpShortlist_handleInput(value);
    },
    isVehicleShortlisted(vehicle) {
      return this.vrpShortlist_isVehicleShortlisted(vehicle);
    },
    isShortlistAtMax() {
      return this.vrpShortlist_isShortlistAtMax();
    },
    formatWordsAsSentences,
    formatVehicleDescription,

    // Search/Sort mixin wrappers
    handleSort(value) {
      return this.vrpSearch_handleSort(value);
    },
    handleSortOrder(value) {
      return this.vrpSearch_handleSortOrder(value);
    },
    handleSearch() {
      return this.vrpSearch_handleSearch();
    },
    handlePageSize() {
      return this.vrpSearch_handlePageSize();
    },
    onResetFilters() {
      return this.vrpSearch_onResetFilters();
    },
    fetchVehicles() {
      return this.vrpSearch_fetchVehicles();
    },
    initialFetchRatebook() {
      return this.vrpSearch_initialFetchRatebook();
    },
    fetchUserShortlist() {
      return this.vrpSearch_fetchUserShortlist();
    },
    calculateTotalPages() {
      return this.vrpSearch_calculateTotalPages();
    },
    handleSelectRepayment(repayment) {
      return this.vrpSearch_handleSelectRepayment(repayment);
    },

    /**
     * Set search options and sync repayment (Weekly/Monthly) from budgetType when present.
     * Used instead of a watcher so repayment stays in sync when search options come from
     * URL, refine search submit, or filter dialog.
     * All updates to searchOptions should go through this (except reset, which sets repayment explicitly).
     */
    applySearchOptions(value) {
      if (value == null) return;
      this.searchOptions = value;
      if (value.budgetType === 'weekly' || value.budgetType === 'monthly') {
        this.repayment = value.budgetType;
      }
    },

    // Modal mixin wrappers
    showInfo(value) {
      return this.vrpModal_showInfo(value);
    },
    showEnquireNow(value, vehicle) {
      return this.vrpModal_showEnquireNow(value, vehicle);
    },
    handleViewShortlist() {
      return this.vrpModal_handleViewShortlist();
    },
    handleLeaseInclusionDialog() {
      return this.vrpModal_handleLeaseInclusionDialog();
    }
  },
  async created() {
    try {
      this.sliderIcon = evaOptions2;
      this.chevronIcon = evaChevronRight;
      this.disableShortlist = this.vrpShortlist_isShortlistAtMax();
      this.applySearchOptions(this.queryParams);

      // Set leaseTerm BEFORE initialFetchRatebook to avoid watcher firing during initialization
      // The watcher is guarded by isInitializingLeaseTerm flag, so it won't trigger fetchRatebook
      this.leaseTerm = this.getDefaultLeaseTermOption;

      // Only fetch shortlist if it has items - avoid unnecessary API call
      // Check both local shortlist and Vuex getter to ensure we have the latest data
      const hasShortlistItems = (this.shortlist && this.shortlist.length > 0) || (this.shortlisted && this.shortlisted.length > 0);
      if (hasShortlistItems) {
        await this.vrpSearch_fetchUserShortlist();
      }

      await this.vrpSearch_initialFetchRatebook();
    } catch (error) {
      // eslint-disable-next-line
      console.error('Error during VehicleResultPage initialization:', error);
    } finally {
      // Always mark initialization as complete, even if errors occurred
      // Use $nextTick to ensure all async operations complete before unguarding watcher
      await this.$nextTick();
      this.isInitializingLeaseTerm = false;
    }
  },
  components: {
    QSelect,
    QPagination,
    VehicleResultCard,
    RefineSearchContainer,
    LeaseInclusionVehicleResult,
    CustomExpansionItem,
    LeaseInclusionModal,
    InformationModal,
    EnquireNowModal,
    NoResults,
    VehicleResultFilterDrawer,
    MobileNavigation,
    VehicleResultActions,
    LoadingSpinner,
    ShortlistSection
  }
};
</script>
